import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, Loader2, ArrowRight, RefreshCw,
} from 'lucide-react';

/**
 * /checkin/payment-status
 * ─────────────────────────────────────────────────────────────────
 * Interstitial page shown AFTER the payment gateway redirects back to us.
 * Verifies the actual transaction status with the server BEFORE confirming
 * the booking by forwarding the user to /checkin/success.
 *
 * If the payment is still 'pending' (e.g. UPI awaiting), polls every 3s
 * up to 30s before giving up and routing the user to /checkin/pending.
 */
export default function PaymentStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const ref     = searchParams.get('ref');

  const [payment, setPayment] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const pollTimerRef = useRef(null);

  // ── Fetch + poll payment status ──────────────────────────────────────
  const fetchStatus = async () => {
    try {
      const res = await publicApi.getPaymentStatus(orderId);
      setPayment(res.data.payment);
      setCheckin(res.data.checkin);
      setError(null);
      return res.data.payment;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load payment.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId) { navigate('/checkin'); return; }
    let alive = true;

    (async () => {
      const p = await fetchStatus();
      if (!alive) return;
      if (p?.status === 'pending' || p?.status === 'initiated') {
        pollTimerRef.current = setInterval(async () => {
          setPollCount((c) => c + 1);
          const np = await fetchStatus();
          if (!alive) return;
          if (np?.status && np.status !== 'pending' && np.status !== 'initiated') {
            clearInterval(pollTimerRef.current);
          }
        }, 3000);
      }
    })();

    return () => {
      alive = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [orderId]);

  // Auto-stop polling after 30s
  useEffect(() => {
    if (pollCount >= 10 && pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }
  }, [pollCount]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="text-center" data-testid="status-loading">
          <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin mx-auto mb-3" />
          <p className="text-[#B9B9B9] text-sm">Verifying your payment…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="text-center max-w-sm px-4" data-testid="status-error">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-[#F2F2F2] font-semibold mb-2">Couldn’t verify payment</p>
          <p className="text-[#B9B9B9] text-sm mb-4">{String(error)}</p>
          <Button onClick={() => navigate('/checkin')} className="btn-gold">Back to Check-in</Button>
        </div>
      </div>
    );
  }

  const status     = payment?.status;
  const isPaid     = status === 'paid';
  const isPending  = status === 'pending' || status === 'initiated';
  const isFailed   = status === 'failed';
  const isCancel   = status === 'cancelled';
  const provider   = (payment?.provider || '').toLowerCase();
  const providerLabel = provider === 'paytm' ? 'Paytm' : 'Gateway';
  const raw        = payment?.raw_response || {};
  const txnId      = payment?.transaction_id;
  const bankTxnId  = raw.bank_txn_id || raw.bank_ref_no;
  const paymentMode= raw.payment_mode;
  const bankName   = raw.bank_name;
  const failureMsg = raw.result_msg || raw.failure_message;
  const amount     = payment?.amount || 0;
  const refDisplay = ref || checkin?.reference_id || '';

  // ── Status icon + colour ─────────────────────────────────────────────
  const palette = isPaid    ? { bg: '#0D3320', border: '#2FBF71', text: '#7EF0B1', Icon: CheckCircle2 }
               : isPending ? { bg: '#332710', border: '#D4A843', text: '#F1E2B8', Icon: Clock }
               : isCancel  ? { bg: '#2A220F', border: '#7A6433', text: '#E8D7AA', Icon: AlertTriangle }
                           : { bg: '#3A1414', border: '#C84545', text: '#FFB8B8', Icon: XCircle };

  const headline = isPaid    ? 'Payment Successful'
                 : isPending ? 'Payment Pending'
                 : isCancel  ? 'Payment Cancelled'
                             : 'Payment Failed';
  const subline  = isPaid    ? 'Your booking is confirmed.'
                 : isPending ? 'Bank is processing your payment. This usually takes under a minute.'
                 : isCancel  ? 'You cancelled the payment at the gateway. Your booking has not been confirmed.'
                             : (failureMsg || 'The payment did not go through. No amount has been charged.');

  const proceed = () => {
    if (isPaid)       navigate(`/checkin/success?ref=${refDisplay}&order_id=${orderId}`);
    else if (isPending) navigate(`/checkin/pending?ref=${refDisplay}&order_id=${orderId}`);
    else                navigate(`/checkin/failure?ref=${refDisplay}&order_id=${orderId}&reason=${isCancel ? 'cancelled' : 'failed'}`);
  };

  const retry = () => {
    navigate('/checkin');
  };

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center" style={{ backgroundColor: '#0A0A0A' }} data-testid="payment-status-page">
      <div className="max-w-[480px] w-full px-4 py-8">

        {/* Status hero */}
        <div
          className="rounded-2xl p-6 text-center border-2"
          style={{ backgroundColor: palette.bg, borderColor: palette.border }}
          data-testid={`status-${status}`}
        >
          <palette.Icon className="w-16 h-16 mx-auto mb-3" style={{ color: palette.border }} strokeWidth={1.8} />
          <h1 className="font-['EB_Garamond'] text-2xl font-semibold mb-1" style={{ color: palette.text }}>
            {headline}
          </h1>
          <p className="text-sm" style={{ color: palette.text, opacity: 0.85 }}>{subline}</p>
        </div>

        {/* Transaction details */}
        <div className="surface-card card-highlight rounded-xl p-5 mt-4 space-y-3" data-testid="status-details">
          <h2 className="text-[#F2F2F2] text-sm font-semibold uppercase tracking-wider">Transaction Details</h2>
          <Row label="Amount" value={formatCurrency(amount)} testid="detail-amount" highlight />
          <Row label="Status" value={(status || '').toUpperCase()} testid="detail-status" />
          <Row label="Gateway" value={providerLabel} testid="detail-gateway" />
          {txnId      && <Row label="Transaction ID" value={txnId} testid="detail-txn" mono />}
          {bankTxnId  && <Row label="Bank Reference" value={bankTxnId} testid="detail-bank-ref" mono />}
          {paymentMode && <Row label="Payment Method" value={paymentMode} testid="detail-mode" />}
          {bankName   && <Row label="Bank" value={bankName} testid="detail-bank" />}
          <Row label="Order ID" value={orderId} testid="detail-order-id" mono />
          {refDisplay && <Row label="Booking Ref" value={refDisplay} testid="detail-ref" mono />}
        </div>

        {/* Polling indicator */}
        {isPending && pollCount < 10 && (
          <p className="text-center text-[#B9B9B9] text-xs mt-4 flex items-center justify-center gap-2" data-testid="polling-indicator">
            <RefreshCw size={11} className="animate-spin" />
            Auto-refreshing… ({Math.min(pollCount * 3, 30)}s)
          </p>
        )}

        {/* Actions */}
        <div className="mt-5 space-y-2">
          {isPaid && (
            <Button onClick={proceed} className="btn-gold w-full h-11" data-testid="btn-continue">
              Continue to Confirmation <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {isPending && pollCount >= 10 && (
            <Button onClick={proceed} className="btn-gold w-full h-11" data-testid="btn-pending-continue">
              I’ll check back later <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {(isFailed || isCancel) && (
            <>
              <Button onClick={retry} className="btn-gold w-full h-11" data-testid="btn-retry">
                Try a new check-in
              </Button>
              <Button onClick={proceed} variant="outline" className="w-full h-10 bg-transparent border-[#3A2F16] text-[#B9B9B9] hover:bg-[#1F1A10]" data-testid="btn-see-failure">
                See failure details
              </Button>
            </>
          )}
        </div>

        <p className="text-center text-[#4A4A4A] text-xs mt-6">
          Verified via {providerLabel} · Your card details are never stored on our servers.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, testid, mono = false, highlight = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[#B9B9B9] text-xs">{label}</span>
      <span
        data-testid={testid}
        className={`text-sm break-all text-right ${mono ? 'text-mono' : ''} ${highlight ? 'text-[#D4A843] font-semibold' : 'text-[#F2F2F2]'}`}
      >
        {value}
      </span>
    </div>
  );
}
